import React from 'react';
import type { IntlShape } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	fireAnalyticsEvent,
} from '@atlaskit/editor-common/analytics';
import type { ContextualToolbar, ExtensionAPI } from '@atlaskit/editor-common/extensions';
import type { Command, DropdownOptionT } from '@atlaskit/editor-common/types';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import AlignContentBottomIcon from '@atlaskit/icon-lab/core/align-content-bottom';
import AlignContentLeftIcon from '@atlaskit/icon-lab/core/align-content-left';

import { EXTENSION_KEY, EXTENSION_TYPE } from '../nativeTabsConstants.ts';
import type { NativeTabsParameters, TabOrientation } from '../nativeTabsTypes.ts';

const i18n = defineMessages({
	tabOptions: {
		id: 'native-tabs.orientation-toolbar.tab-options.non-final',
		defaultMessage: 'Tab options',
		description: 'Label for the Native Tabs options dropdown in the editor toolbar.',
	},
	horizontal: {
		id: 'native-tabs.orientation-toolbar.horizontal.non-final',
		defaultMessage: 'Horizontal',
		description: 'Dropdown option that displays Native Tabs horizontally.',
	},
	vertical: {
		id: 'native-tabs.orientation-toolbar.vertical.non-final',
		defaultMessage: 'Vertical',
		description: 'Dropdown option that displays Native Tabs vertically.',
	},
});

const TabOptionsIcon = () => <CustomizeIcon label="" />;

const getOrientation = (node: {
	attrs?: { parameters?: NativeTabsParameters };
}): TabOrientation => {
	const parameters = node.attrs?.parameters;
	return parameters?.orientation === 'vertical' ? 'vertical' : 'horizontal';
};

const updateOrientation =
	(
		api: ExtensionAPI,
		localId: string | undefined,
		orientation: TabOrientation,
		createAnalyticsEvent?: CreateUIAnalyticsEvent,
	): Command =>
	() => {
		if (!localId) {
			return false;
		}
		const { node } = api.getNodeWithPosByLocalId(localId);
		if (getOrientation(node) === orientation) {
			return false;
		}
		api.doc.update(localId, (currentNode) => ({
			...currentNode,
			attrs: {
				...currentNode.attrs,
				parameters: {
					...(currentNode.attrs?.parameters as NativeTabsParameters | undefined),
					orientation,
				},
			},
		}));
		const attributes = {
			extensionType: node.attrs.extensionType,
			extensionKey: node.attrs.extensionKey,
			localId,
			currentFramesCount: node.content.childCount,
			changedParams: '~orientation',
			orientation,
		};
		fireAnalyticsEvent(createAnalyticsEvent)({
			payload: {
				action: ACTION.UPDATE_PARAMETERS,
				actionSubject: ACTION_SUBJECT.MULTI_BODIED_EXTENSION,
				eventType: EVENT_TYPE.TRACK,
				attributes,
			},
		});
		return true;
	};

export const getOrientationToolbar = (
	intl: IntlShape,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): ContextualToolbar => ({
	context: {
		type: 'extension',
		nodeType: 'multiBodiedExtension',
		extensionKey: EXTENSION_KEY,
		extensionType: EXTENSION_TYPE,
	},
	toolbarItems: (contextNode, api) => {
		const currentOrientation = getOrientation(contextNode);
		const localId = contextNode.attrs?.localId;
		const options: DropdownOptionT<Command>[] = [
			{
				id: 'native-tabs-orientation-horizontal',
				icon: <AlignContentBottomIcon label="" />,
				title: intl.formatMessage(i18n.horizontal),
				selected: currentOrientation === 'horizontal',
				onClick: updateOrientation(api, localId, 'horizontal', createAnalyticsEvent),
			},
			{
				id: 'native-tabs-orientation-vertical',
				icon: <AlignContentLeftIcon label="" />,
				title: intl.formatMessage(i18n.vertical),
				selected: currentOrientation === 'vertical',
				onClick: updateOrientation(api, localId, 'vertical', createAnalyticsEvent),
			},
		];
		return [
			{
				key: `native-tabs-orientation-dropdown-${currentOrientation}`,
				id: `native-tabs-orientation-dropdown-${currentOrientation}`,
				testId: 'native-tabs-orientation-dropdown-trigger',
				type: 'dropdown',
				title: intl.formatMessage(i18n.tabOptions),
				iconBefore: TabOptionsIcon,
				showSelected: true,
				options,
			},
		];
	},
});
